import { MapPin, Navigation } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { api, type EndpointRef, type OtdrResponse } from '../api'
import { useApp } from '../context'
import { fmtM } from '../format'
import { PanelShell } from './PanelShell'
import { Button, Callout, KeyValue, Section } from './ui'

type StartKind = 'pon' | 'core'

export function OtdrPanel({ from }: { from?: EndpointRef }) {
  const { network, setHighlight, focusOn, open } = useApp()
  const [oltId, setOltId] = useState(from?.kind === 'pon' ? from.oltId : network.olts[0]?.id ?? '')
  const olt = network.olts.find((o) => o.id === oltId)
  const [startKind, setStartKind] = useState<StartKind>(from?.kind === 'core' ? 'core' : 'pon')
  const [port, setPort] = useState(from?.kind === 'pon' ? from.port : 1)
  const [cableId, setCableId] = useState(from?.kind === 'core' ? from.cableId : network.cables[0]?.id ?? '')
  const [core, setCore] = useState(from?.kind === 'core' ? from.core : 1)
  const [end, setEnd] = useState<'A' | 'B'>(from?.kind === 'core' ? from.end : 'A')
  const [distance, setDistance] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<OtdrResponse | null>(null)

  const cable = network.cables.find((c) => c.id === cableId)
  const sortedCables = useMemo(() => [...network.cables].sort((a, b) => a.name.localeCompare(b.name)), [network.cables])
  const dist = Number(distance)
  const distError = distance === '' ? '' : !(dist >= 0 && dist <= 100000) ? 'Enter metres between 0 and 100 000' : ''

  useEffect(() => setHighlight({}), [setHighlight])

  const run = async (e: React.FormEvent) => {
    e.preventDefault()
    if (distance === '' || distError) return
    const start: EndpointRef = startKind === 'pon' ? { kind: 'pon', oltId: olt!.id, port } : { kind: 'core', cableId, core, end }
    setBusy(true)
    setError(null)
    try {
      const r = await api.otdr(start, dist)
      setResult(r)
      if (r.status === 'located' || r.status === 'at-node') {
        setHighlight({ cableIds: r.walkedCableIds, point: { lngLat: r.point, radiusM: r.uncertaintyM, label: 'OTDR event' }, tone: 'danger' })
        focusOn(r.point, 17)
      } else {
        setHighlight({ cableIds: r.walkedCableIds, tone: 'danger' })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not locate')
      setResult(null)
    } finally {
      setBusy(false)
    }
  }

  const input = 'h-10 w-full rounded-lg border border-border bg-surface px-2 text-sm'
  return (
    <PanelShell title="Locate an OTDR event" subtitle="Turn the distance on the OTDR trace into a place on the map.">
      <Section title="Measurement">
        <form className="space-y-3" onSubmit={run}>
          <fieldset>
            <legend className="mb-1 text-sm font-medium">OTDR connected at</legend>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-1.5">
                <input type="radio" name="start" checked={startKind === 'pon'} onChange={() => setStartKind('pon')} /> OLT PON port
              </label>
              <label className="flex items-center gap-1.5">
                <input type="radio" name="start" checked={startKind === 'core'} onChange={() => setStartKind('core')} /> A cable core
              </label>
            </div>
          </fieldset>
          {startKind === 'pon' ? (
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <label htmlFor="otdr-olt" className="mb-1 block text-sm font-medium">
                  OLT
                </label>
                <select id="otdr-olt" className={input} value={oltId} onChange={(e) => setOltId(e.target.value)}>
                  {network.olts.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name} · {network.nodes.find((n) => n.id === o.nodeId)?.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="otdr-port" className="mb-1 block text-sm font-medium">
                  PON port
                </label>
                <select id="otdr-port" className={input} value={port} onChange={(e) => setPort(Number(e.target.value))}>
                  {Array.from({ length: olt?.ponPortCount ?? 0 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      PON {i + 1}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-6 gap-2">
              <div className="col-span-6">
                <label htmlFor="otdr-cable" className="mb-1 block text-sm font-medium">
                  Cable
                </label>
                <select id="otdr-cable" className={input} value={cableId} onChange={(e) => setCableId(e.target.value)}>
                  {sortedCables.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.fiberCount}F)
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label htmlFor="otdr-core" className="mb-1 block text-sm font-medium">
                  Core
                </label>
                <select id="otdr-core" className={input} value={core} onChange={(e) => setCore(Number(e.target.value))}>
                  {Array.from({ length: cable?.fiberCount ?? 1 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-4">
                <label htmlFor="otdr-end" className="mb-1 block text-sm font-medium">
                  Shooting from
                </label>
                <select id="otdr-end" className={input} value={end} onChange={(e) => setEnd(e.target.value as 'A' | 'B')}>
                  <option value="A">{network.nodes.find((n) => n.id === cable?.fromNodeId)?.name} (A end)</option>
                  <option value="B">{network.nodes.find((n) => n.id === cable?.toNodeId)?.name} (B end)</option>
                </select>
              </div>
            </div>
          )}
          <div>
            <label htmlFor="otdr-distance" className="mb-1 block text-sm font-medium">
              Event distance on the OTDR (metres of fibre)
            </label>
            <input
              id="otdr-distance"
              inputMode="decimal"
              className={input}
              value={distance}
              onChange={(e) => setDistance(e.target.value)}
              placeholder="e.g. 1234.5"
              aria-invalid={Boolean(distError)}
              aria-describedby="otdr-distance-help"
            />
            <p id="otdr-distance-help" className={`mt-1 text-xs ${distError ? 'text-danger' : 'text-muted'}`}>
              {distError || 'Use the same IOR as the cable datasheet. Slack loops and fibre overlength are taken into account.'}
            </p>
          </div>
          <Button type="submit" variant="primary" busy={busy} disabled={distance === '' || Boolean(distError)}>
            <MapPin className="h-4 w-4" aria-hidden /> Locate
          </Button>
        </form>
      </Section>
      <div aria-live="polite">
        {error && (
          <div className="px-4 pb-4">
            <Callout tone="danger" title="Could not locate">
              {error}
            </Callout>
          </div>
        )}
        {result && <OtdrResult r={result} onOpen={(id) => open({ kind: 'node', id })} />}
      </div>
    </PanelShell>
  )
}

function OtdrResult({ r, onOpen }: { r: OtdrResponse; onOpen: (nodeId: string) => void }) {
  if (r.status === 'located') {
    const [lng, lat] = r.point
    return (
      <Section title="Result">
        <Callout tone="danger" title={r.slackNodeName ? `Inside the slack loop at ${r.slackNodeName}` : `${fmtM(r.routeFromEntryM)} past ${r.entryNodeName}`}>
          On {r.cableName} core {r.core}, heading to {r.exitNodeName}. Search ±{r.uncertaintyM} m around the red pin.
        </Callout>
        <div className="mt-3">
          <KeyValue
            rows={[
              ['From entry node', `${fmtM(r.routeFromEntryM)} along the route (${r.entryNodeName})`],
              ['To next node', `${fmtM(r.routeToExitM)} (${r.exitNodeName})`],
              ['Fibre into this cable', fmtM(r.fiberIntoCableM)],
              ['Coordinates', `${lat.toFixed(6)}, ${lng.toFixed(6)}`],
            ]}
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <a
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-accent px-2.5 text-[13px] font-medium text-accent-fg"
            href={`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`}
            target="_blank"
            rel="noreferrer"
          >
            <Navigation className="h-4 w-4" aria-hidden /> Directions to the pin
          </a>
          <Button size="sm" onClick={() => onOpen(r.entryNodeId)}>
            Open {r.entryNodeName}
          </Button>
        </div>
      </Section>
    )
  }
  if (r.status === 'at-node') {
    return (
      <Section title="Result">
        <Callout tone="danger" title={`At ${r.nodeName} (±${r.uncertaintyM} m)`}>
          The event is at the end of the traced fibre. Inspect the splices and connectors at this node first.
        </Callout>
      </Section>
    )
  }
  if (r.status === 'beyond-splitter') {
    return (
      <Section title="Result">
        <Callout tone="warn" title={`Beyond ${r.splitterName}`}>
          The event is {fmtM(r.remainingM)} past the splitter at {r.nodeName}. An OTDR cannot tell splitter branches apart: shoot again from the NAP side towards the splitter.
        </Callout>
      </Section>
    )
  }
  return (
    <Section title="Result">
      <Callout tone="warn" title="Distance is past the end of the recorded fibre">
        The traced fibre ends at {r.endNodeName}, {fmtM(r.overshootM)} short of the event. Check the IOR setting, or a splice is missing from the records beyond that point.
      </Callout>
    </Section>
  )
}
